import pMap from 'p-map'
import { visorPRORestClient } from '../../util/api'
import { makeObservable, observable, runInAction } from 'mobx'

const ignoredFileNames = new Set(['.DS_Store'])
const attachmentKeys = ['images', 'attachments']

const getBaseDirectory = (path: string) => {
    // 1. group files in the same directory
    // remove file name
    const index = path.lastIndexOf('/')
    if (index !== -1) {
        path = path.slice(0, index)
    }

    // remove file directories
    for (const key of attachmentKeys) {
        if (path.endsWith(key)) {
            path = path.slice(0, -key.length)
            break
        }
    }

    // remove trailing '/'
    if (path.endsWith('/')) {
        path = path.slice(0, -1)
    }

    return path
}

export class KnowledgeArticlesUploadStore {
    public isUploading = false
    public uploadStartDate: Date | undefined
    public lastUpdateDate: Date | undefined
    public totalItemsToUpload = 0
    public totalSuccesses = 0
    public totalFailures = 0

    // to print avg duration
    public totalDuration = 0

    constructor() {
        makeObservable(this, {
            isUploading: observable,
            uploadStartDate: observable,
            totalItemsToUpload: observable,
            totalSuccesses: observable,
            totalFailures: observable,
            totalDuration: observable,
        })
    }

    public handleFileChange = async (files: FileList | null) => {
        if (!files || this.isUploading) return

        runInAction(() => {
            this.isUploading = true
            this.uploadStartDate = new Date()
            this.lastUpdateDate = new Date()
        })

        try {
            // 1. gather all files for each ka before processing them
            const filesByBaseDirectory: { [key: string]: File[] } = {}

            Array.from(files).forEach((file) => {
                if (ignoredFileNames.has(file.name)) return

                const path = getBaseDirectory(file.webkitRelativePath)
                if (!filesByBaseDirectory[path]) {
                    filesByBaseDirectory[path] = []
                }
                filesByBaseDirectory[path].push(file)
            })

            runInAction(() => {
                this.totalItemsToUpload =
                    Object.keys(filesByBaseDirectory).length
                this.totalSuccesses = 0
                this.totalFailures = 0
                this.totalDuration = 0
            })

            // 2. upload a few at a time
            await pMap(
                Object.values(filesByBaseDirectory),
                async (files) => {
                    try {
                        const start = performance.now()
                        await this.uploadFile(files)
                        const end = performance.now()
                        runInAction(() => {
                            this.totalSuccesses++
                            this.totalDuration += end - start
                            this.lastUpdateDate = new Date()
                        })
                    } catch (e) {
                        runInAction(() => {
                            this.totalFailures++
                            this.lastUpdateDate = new Date()
                        })
                    }
                },
                { concurrency: 5, stopOnError: false },
            )
        } catch (e) {
            console.log(e)
        } finally {
            runInAction(() => {
                this.isUploading = false
            })
        }
    }

    private uploadFile = async (files: File[]) => {
        for (
            let remainingRetries = 3;
            remainingRetries >= 0;
            remainingRetries--
        ) {
            try {
                await visorPRORestClient.knowledgeArticles.upload(files)
                break
            } catch (e) {
                continue
            }
        }
    }
}
